import { useState } from "react";
import { Modal, Button, Upload, Alert, List, Typography, Tag, Collapse, message } from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useAxios from "../../hooks/useAxios";

const { Dragger } = Upload;
const { Text } = Typography;

const SAMPLE_FILE_URL = "/samples/fsr/fsr-import-all-sample.xlsx";
const SAMPLE_FILE_NAME = "fsr-import-all-sample.xlsx";

const HIDDEN_PREVIEW_KEYS = new Set([
  "id",
  "employee_id",
  "created_at",
  "updated_at",
]);

// Compact, section-agnostic one-line summary of a mapped row's values —
// mirrors FsrImportModal's helper of the same name.
const summarizeRowData = (data) => {
  if (!data) return "";
  return Object.entries(data)
    .filter(([key, value]) => !HIDDEN_PREVIEW_KEYS.has(key) && value !== null && value !== "")
    .map(([, value]) => (typeof value === "boolean" ? (value ? "Yes" : "No") : value))
    .join(" · ");
};

const PREVIEW_STATUS_META = {
  will_import: { color: "success", label: "Will import" },
  duplicate: { color: "default", label: "Duplicate — will skip" },
  invalid: { color: "error", label: "Invalid" },
};

// Companion to FsrImportModal — instead of one section's rows, this posts
// one combined multi-sheet XLSX (one sheet per FSR section) to
// fsr/import-all and shows a per-section breakdown of the results, since a
// single flat imported/skipped/failed count wouldn't say which section(s)
// had problems.
//
// Flow mirrors FsrImportModal: pick a file -> "Preview" (preview=1) shows a
// per-section, row-by-row breakdown of what would happen -> "Confirm Import"
// commits for real. The Faculty-Info-mismatch safety check (backend 409 +
// requiresConfirmation) can fire on either the preview or the confirm
// request; once the user says "Continue Anyway" we remember that for the
// rest of this file's session so they're never asked twice.
const FsrBulkImportModal = ({ open, onClose, onImported }) => {
  const { fetchData } = useAxios();
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [confirmMismatch, setConfirmMismatch] = useState(false);

  const resetAll = () => {
    setImportFile(null);
    setPreviewResult(null);
    setImportResult(null);
    setConfirmMismatch(false);
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const handleChangeFile = () => {
    setImportFile(null);
    setPreviewResult(null);
    setConfirmMismatch(false);
  };

  const runRequest = async (preview, mismatchOverride) => {
    const formData = new FormData();
    formData.append("file", importFile);
    if (preview) {
      formData.append("preview", "1");
    }
    if (mismatchOverride ?? confirmMismatch) {
      formData.append("confirm_mismatch", "1");
    }

    setImporting(true);
    try {
      const res = await fetchData({
        url: "fsr/import-all",
        method: "POST",
        data: formData,
      });
      const data = res?.data ?? res;
      if (preview) {
        setPreviewResult(data);
      } else {
        setImportResult(data);
        // Refresh every section table on every successful commit, not just
        // when totalImported > 0 — e.g. a re-run that turns out to be all
        // duplicates should still resync with the server's current state.
        onImported?.(data);
      }
    } catch (e) {
      const errBody = e?.response?.data;
      if (e?.response?.status === 409 && errBody?.requiresConfirmation) {
        Modal.confirm({
          title: "This file may belong to someone else",
          content: errBody.message,
          okText: "Continue Anyway",
          cancelText: "Cancel",
          onOk: () => {
            setConfirmMismatch(true);
            runRequest(preview, true);
          },
        });
        return;
      }

      const errMsg = errBody?.message || "Import failed. Please try again.";
      message.error(errMsg);
    } finally {
      setImporting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (!importFile) {
      message.warning("Please select a file first.");
      return;
    }
    runRequest(!previewResult);
  };

  const activeResult = importResult || previewResult;
  const sectionEntries = activeResult?.sections
    ? Object.entries(activeResult.sections).map(([sheetName, section]) => ({ sheetName, ...section }))
    : [];

  return (
    <Modal
      title="Import All Sections"
      open={open}
      onCancel={handleClose}
      width={640}
      destroyOnClose
      footer={
        importResult ? (
          <Button type="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {previewResult && (
              <Button onClick={handleChangeFile}>Change File</Button>
            )}
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handlePrimaryAction}
              loading={importing}
              disabled={!importFile}
            >
              {previewResult ? "Confirm Import" : "Preview"}
            </Button>
          </>
        )
      }
    >
      {!activeResult ? (
        <>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Not sure about the format?&nbsp;
            </Text>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              href={SAMPLE_FILE_URL}
              download={SAMPLE_FILE_NAME}
              style={{ padding: 0, fontSize: 13 }}
            >
              Download sample XLSX
            </Button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              One Excel file with a separate sheet per section — Teaching
              Load (In UPOU / Other UP / Outside UP), Research, Administrative
              Work, Tutorial Work, Extension Service, Advising, Conference
              Presentations, Seminar Workshops, and Consultation Hours. A
              workbook doesn't need every sheet filled in — sections left
              blank or missing are simply skipped.
            </Text>
          </div>

          <Dragger
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              setImportFile(file);
              return false;
            }}
            onRemove={() => setImportFile(null)}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#862633" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag an <strong>.xlsx</strong> file here
            </p>
            <p className="ant-upload-hint">Max file size: 5 MB</p>
          </Dragger>
        </>
      ) : importResult ? (
        <div>
          <div style={{ marginBottom: 12 }}>
            {importResult.totalImported > 0 && (
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={`${importResult.totalImported} ${importResult.totalImported === 1 ? "row" : "rows"} imported across ${sectionEntries.length} ${sectionEntries.length === 1 ? "section" : "sections"}.`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.totalFailed > 0 && (
              <Alert
                type="warning"
                icon={<CloseCircleOutlined />}
                message={`${importResult.totalFailed} ${importResult.totalFailed === 1 ? "row" : "rows"} could not be imported.`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.totalSkipped > 0 && (
              <Alert
                type="info"
                icon={<InfoCircleOutlined />}
                message={`${importResult.totalSkipped} duplicate ${importResult.totalSkipped === 1 ? "row" : "rows"} skipped (already in your profile).`}
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {importResult.totalImported === 0 &&
              importResult.totalFailed === 0 &&
              !importResult.totalSkipped && (
                <Alert
                  type="info"
                  message="No data rows were found in any recognized section sheet."
                  showIcon
                />
              )}
          </div>

          {sectionEntries.length > 0 && (
            <Collapse
              size="small"
              items={sectionEntries.map((section, i) => ({
                key: i,
                label: (
                  <span>
                    <Text strong>{section.label}</Text>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      {section.imported} imported
                      {section.skipped > 0 ? `, ${section.skipped} skipped` : ""}
                      {section.failed > 0 ? `, ${section.failed} failed` : ""}
                    </Text>
                  </span>
                ),
                children:
                  section.errors && section.errors.length > 0 ? (
                    <List
                      size="small"
                      dataSource={section.errors}
                      renderItem={(err) => (
                        <List.Item>
                          <div>
                            <Text strong style={{ fontSize: 13 }}>
                              Row {err.row}:
                            </Text>
                            <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                              {err.errors.map((e, j) => (
                                <li key={j}>
                                  <Text type="danger" style={{ fontSize: 12 }}>
                                    {e}
                                  </Text>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      No row errors.
                    </Text>
                  ),
              }))}
            />
          )}
        </div>
      ) : (
        <div>
          <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
            Nothing has been saved yet — review what this file will do, then
            click Confirm Import to proceed, or Change File to pick a
            different one.
          </Text>
          <div style={{ marginBottom: 12 }}>
            <Alert
              type="info"
              message={`${previewResult.totalImported} to import, ${previewResult.totalSkipped} duplicate, ${previewResult.totalFailed} invalid, across ${sectionEntries.length} ${sectionEntries.length === 1 ? "section" : "sections"}.`}
              showIcon
            />
          </div>

          {sectionEntries.length > 0 && (
            <Collapse
              size="small"
              items={sectionEntries.map((section, i) => ({
                key: i,
                label: (
                  <span>
                    <Text strong>{section.label}</Text>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      {section.imported} to import
                      {section.skipped > 0 ? `, ${section.skipped} duplicate` : ""}
                      {section.failed > 0 ? `, ${section.failed} invalid` : ""}
                    </Text>
                  </span>
                ),
                children: (
                  <List
                    size="small"
                    dataSource={section.rows || []}
                    renderItem={(row) => {
                      const meta = PREVIEW_STATUS_META[row.status] || {};
                      return (
                        <List.Item>
                          <div style={{ width: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <Text strong style={{ fontSize: 12 }}>
                                Row {row.row}
                              </Text>
                              <Tag color={meta.color} style={{ margin: 0, fontSize: 11 }}>
                                {meta.label}
                              </Tag>
                            </div>
                            {row.status === "invalid" ? (
                              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                                {row.errors.map((e, j) => (
                                  <li key={j}>
                                    <Text type="danger" style={{ fontSize: 12 }}>
                                      {e}
                                    </Text>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {summarizeRowData(row.data)}
                              </Text>
                            )}
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                ),
              }))}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default FsrBulkImportModal;
